import C from "@/components/ActionMenu.module.css";

import {
    actionIsDisabled,
    actionIsLinkProps,
    actionIsRouterLink,
    buildHrefFromAction,
    DISABLED,
    normalizeAction,
    type UniversalAction,
} from "@/components/Action.tsx";
import { MaskIcon } from "@/components/icon/MaskIcon.tsx";
import { TODO } from "@/dev/Todo.tsx";
import { isTodoFn } from "@/dev/todoFn.ts";
import { useRouter } from "@tanstack/react-router";
import { notNull } from "@warrenio/utils/notNull";
import { discardPromise } from "@warrenio/utils/promise/discardPromise";
import { exhaustiveSwitchCheck } from "@warrenio/utils/unreachable";
import { useMemo, type PropsWithChildren, type ReactNode } from "react";
import {
    Header,
    Menu,
    MenuItem,
    MenuTrigger,
    Popover,
    SubmenuTrigger,
    type Key,
    type MenuItemProps,
    type PopoverProps,
    type SelectionMode,
} from "react-aria-components";

export interface ActionMenuItemProps {
    id: Key;

    title: ReactNode;
    /** Required for accessibility when {@link title} is not a string. */
    textValue?: string;

    icon?: string;
    image?: ReactNode;
    action?: UniversalAction;
    childItems?: ActionMenuItemProps[];
}

export interface ActionMenuContentProps<T extends ActionMenuItemProps> {
    header?: ReactNode;

    placement?: PopoverProps["placement"];

    /** Supports a function for lazy item creation */
    items: T[] | (() => T[]);
    onAction?: (item: T) => void;

    selectedKeys?: Key[];
    selectionMode?: SelectionMode;
}

function ActionMenuItem({
    id,
    title,
    textValue,
    image,
    icon,
    isChecked = false,
    action: actionProp,
}: ActionMenuItemProps & { isChecked?: boolean }) {
    const router = useRouter();
    const linkProps = useMemo((): MenuItemProps | undefined => {
        const action = normalizeAction(actionProp);
        if (action === undefined || actionIsDisabled(action)) {
            return undefined;
        } else if (actionIsLinkProps(action)) {
            return action;
        } else if (actionIsRouterLink(action)) {
            return { href: buildHrefFromAction(router, action), target: action.target, routerOptions: action };
        } else if (typeof action === "function") {
            return {
                onAction: () => discardPromise(action()),
            };
        } else {
            exhaustiveSwitchCheck(action);
        }
    }, [router, actionProp]);

    return (
        <MenuItem
            id={id}
            className={C.ActionMenuItem}
            isDisabled={actionIsDisabled(actionProp)}
            textValue={textValue ?? (typeof title === "string" ? title : undefined)}
            {...linkProps}
        >
            {(icon != null || image != null) && (
                <div className={C.ItemIcon}>
                    {icon != null && <MaskIcon className={icon} />}
                    {image}
                </div>
            )}
            <div className={C.title}>
                {title}
                {isTodoFn(actionProp) && <TODO small />}
            </div>
            {isChecked && <MaskIcon className="jp-icon-checkmark color-success size-1rem" />}
        </MenuItem>
    );
}

export function ActionMenuContent<T extends ActionMenuItemProps>({
    header,
    items,
    onAction,
    selectedKeys,
    selectionMode = "single",
}: ActionMenuContentProps<T>) {
    function makeMenuItem(item: ActionMenuItemProps) {
        const { id, childItems } = item;
        if (childItems != null) {
            return (
                <SubmenuTrigger key={id}>
                    <ActionMenuItem action={childItems.length ? undefined : DISABLED} {...item} />
                    <WPopover offset={8}>{makeMenu(childItems as T[])}</WPopover>
                </SubmenuTrigger>
            );
        }
        return <ActionMenuItem key={id} {...item} isChecked={selectedKeys?.includes(id)} />;
    }

    function makeMenu(items: T[]) {
        const menuItems = items.map(makeMenuItem);
        return (
            <Menu
                onAction={
                    onAction
                        ? (key) => {
                              const item = notNull(items.find((item) => item.id === key));
                              onAction(item);
                          }
                        : undefined
                }
                selectedKeys={selectedKeys}
                selectionMode={selectionMode}
                className={C.Scrollable}
            >
                {menuItems}
            </Menu>
        );
    }

    return (
        <>
            {!!header && <Header className={C.ActionMenuHeader}>{header}</Header>}
            {makeMenu(typeof items === "function" ? items() : items)}
        </>
    );
}

export function WPopover(props: PopoverProps) {
    // Tweak offset to prevent gap between button and popover.
    return <Popover className={C.ActionMenu} offset={2} {...props} />;
}

export function ActionMenu<T extends ActionMenuItemProps>({
    children,
    placement,
    ...props
}: PropsWithChildren<ActionMenuContentProps<T>>) {
    return (
        <MenuTrigger>
            {children}
            <WPopover placement={placement}>
                <ActionMenuContent {...props} />
            </WPopover>
        </MenuTrigger>
    );
}
